import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, CommandMenu, FormText, Popover } from '@milanote/roma';
import { DotsHorizontal } from '@untitledui/icons';

import { cx } from '../../../../../../../common/@roma/src/utils/cx';
import { Label } from '../../../../../../../common/labels/labelTypes';
import { useTranslator } from '../../../../../../components/i18n/useTranslator';
import LabelColorPicker from './LabelColorPicker';
import { useDispatch } from 'react-redux';
import { deleteLabel, updateLabel } from '../../../../../../../common/labels/labelActions';
import { getLabelUsageData } from '../../../../../../element/labels/labelsService';
import CustomColorInput from '../../colorTools/CustomColorInput';
import { getColorDefaultHexValue } from '../../../../../../../common/colors/elementColorFormatUtil';
import { LabeledElementsData } from '../../../../../../../common/api/labels/labelElementsRetrievalRouteTypes';
import LabelListDeletePopover from './LabelListDeletePopover';

type Props = {
    label: Label;
    isHovered: boolean;
    onRename: () => void;
};

const LabelListElementActions: React.FC<Props> = ({ label, isHovered, onRename }) => {
    const dispatch = useDispatch();
    const actionsButtonRef = useRef<HTMLButtonElement>(null);
    const customColorInputRef = useRef<CustomColorInput>(null);
    const [labeledElements, setLabeledELements] = useState<LabeledElementsData[]>([]);
    const [isActionsOpen, setIsActionsOpen] = useState<boolean>(false);
    const translate = useTranslator('popup', 'labels.edit');

    useEffect(() => {
        if (!isActionsOpen) return;

        const getLabelUsageText = async () => {
            const labelUsageData = await getLabelUsageData(label._id);

            setLabeledELements(labelUsageData);
        };

        getLabelUsageText();
    }, [isActionsOpen, label._id]);

    const labelUsageText = useMemo(() => {
        if (labeledElements.length === 0) return null;

        return translate(
            'used',
            { count: labeledElements.length },
            {
                components: {
                    usageLink: <a href="#" onClick={(e) => e.preventDefault()} />,
                },
            },
        );
    }, [labeledElements.length, translate]);

    const handleRename = () => {
        setIsActionsOpen(false);
        onRename();
    };

    const handleDelete = () => {
        dispatch(deleteLabel(label._id));
        setIsActionsOpen(false);
    };

    const handleCustomColorPickerOpen = () => {
        customColorInputRef.current?.open();
    };

    const handleCustomColorChange = (color: string | null) => {
        if (!color) return;

        dispatch(updateLabel(label._id, { color: getColorDefaultHexValue(color) }));
    };

    return (
        <>
            <Button
                ref={actionsButtonRef}
                color="tertiary"
                size="sm"
                aria-label={`Open actions for ${label.name}`}
                className="LabelListElementActions ml-auto data-icon-only:p-0"
                onClick={() => setIsActionsOpen((currentIsOpen) => !currentIsOpen)}
                iconLeading={
                    <DotsHorizontal className={cx('size-4 text-quaternary', isActionsOpen && 'text-fg-primary')} />
                }
            />

            <Popover
                triggerRef={actionsButtonRef}
                placement="end top"
                offset={14}
                crossOffset={-14}
                className="w-max-55 w-min-48"
                isOpen={isActionsOpen}
                onOpenChange={setIsActionsOpen}
            >
                <Popover.Body className="">
                    <CommandMenu aria-label={`${label.name} actions`} size="sm">
                        <CommandMenu.Item
                            id={`rename-${label._id}`}
                            label={translate('rename')}
                            onAction={handleRename}
                        />

                        <CommandMenu.Submenu clickOnlyTrigger>
                            <CommandMenu.Item id={`change-color-${label._id}`} label={translate('changeColor')} />
                            <CommandMenu.SubmenuPopover className="flex">
                                <Popover.Body className="flex h-full items-center justify-center p-4">
                                    <CustomColorInput
                                        className="absolute opacity-0 pointer-events-none"
                                        ref={customColorInputRef}
                                        initialColor={label.color}
                                        setColor={handleCustomColorChange}
                                    />
                                    <LabelColorPicker
                                        label={label}
                                        openCustomColorPicker={handleCustomColorPickerOpen}
                                    />
                                </Popover.Body>
                            </CommandMenu.SubmenuPopover>
                        </CommandMenu.Submenu>

                        <CommandMenu.Submenu clickOnlyTrigger>
                            <CommandMenu.Item id={`delete-${label._id}`} label={translate('delete')} destructive />
                            <CommandMenu.SubmenuPopover className="w-[220px]">
                                <LabelListDeletePopover onDelete={handleDelete} />
                            </CommandMenu.SubmenuPopover>
                        </CommandMenu.Submenu>
                    </CommandMenu>
                </Popover.Body>

                {labeledElements.length > 0 && (
                    <CommandMenu.Footer>
                        <FormText size="sm" color="quaternary">
                            {labelUsageText}
                        </FormText>
                    </CommandMenu.Footer>
                )}
            </Popover>
        </>
    );
};

export default LabelListElementActions;
